import Link from 'next/link';
import type { MediaItem } from '@akademi/site-builder-runtime';

/**
 * Theme-overridable video card. Four visual registers:
 * {@code youtube} / {@code minimal} / {@code poster} / {@code cinematic}.
 */
export type VideoCardVariant = 'youtube' | 'minimal' | 'poster' | 'cinematic';

export interface VideoCardProps {
  item: MediaItem;
  variant?: VideoCardVariant;
  href?: string;
  hrefTemplate?: string;
  active?: boolean;
}

function fmtDuration(seconds?: number): string | undefined {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return undefined;
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}

export function VideoCard({
  item,
  variant = 'youtube',
  href,
  hrefTemplate = item.kind === 'series' ? '/tube/series/{slug}' : '/tube/{slug}',
  active = false,
}: VideoCardProps) {
  const finalHref = href ?? hrefTemplate.replace('{slug}', encodeURIComponent(item.slug));
  const ar = variant === 'poster' ? '2 / 3' : variant === 'cinematic' ? '21 / 9' : '16 / 9';
  const duration = fmtDuration(item.durationSeconds);

  return (
    <Link
      href={finalHref}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3, 12px)',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <div
        aria-hidden
        style={{
          aspectRatio: ar,
          borderRadius: 'var(--radius-lg, 16px)',
          background: item.imageUrl
            ? `url(${item.imageUrl}) center/cover no-repeat`
            : 'var(--color-surface-muted, #e4e4e7)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: active
            ? '0 0 0 2px var(--color-brand-500, #3b5bdb)'
            : 'inset 0 0 0 1px var(--color-border-default, rgba(0,0,0,0.06))',
        }}
      >
        {/* Bottom scrim for legibility of the duration badge. */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.34), transparent 42%)',
          }}
        />
        {duration && (
          <span
            style={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              background: 'rgba(0,0,0,0.78)',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              padding: '2px 6px',
              borderRadius: 6,
            }}
          >
            {duration}
          </span>
        )}
      </div>
      {variant !== 'minimal' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div
            style={{
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 600,
              lineHeight: 1.35,
              color: 'var(--color-text-primary, #18181b)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.title}
          </div>
          {item.subtitle && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-secondary, #71717a)',
              }}
            >
              {item.subtitle}
            </div>
          )}
          {item.meta && item.meta.length > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary, #9ca3af)' }}>
              {item.meta.join(' · ')}
            </div>
          )}
        </div>
      )}
    </Link>
  );
}
